import { useState } from "react";
import { Bookmark, CheckCircle2, MessageSquare, Send, Trophy, XCircle } from "lucide-react";
import { toast } from "sonner";
import { TrackerCard } from "@/components/tracker/TrackerCard";
import { TRACKER_STATUSES, useSavedJobs } from "@/lib/useSavedJobs";

const STAGES = {
  Saved: { Icon: Bookmark, color: "#8A94A2", hint: "Roles you want to come back to." },
  Applied: { Icon: Send, color: "#0078D4", hint: "Application submitted on the employer site." },
  Interview: { Icon: MessageSquare, color: "#B18735", hint: "Screens, panels and site visits." },
  Offer: { Icon: Trophy, color: "#0FBF77", hint: "Offers in hand." },
  Closed: { Icon: XCircle, color: "#3C4450", hint: "Withdrawn, declined or filled." },
};

const Column = ({ status, items }) => {
  const { update } = useSavedJobs();
  const [over, setOver] = useState(false);
  const { Icon, color, hint } = STAGES[status];
  const slug = status.toLowerCase();
  const onDrop = async (event) => {
    event.preventDefault();
    setOver(false);
    const savedId = event.dataTransfer.getData("text/plain");
    if (!savedId || items.some((item) => item.id === savedId)) return;
    try { await update(savedId, { status }); toast.success(`Moved to ${status}.`); } catch (error) { toast.error(error.message || "Could not move this job."); }
  };
  return (
    <section data-testid={`tracker-column-${slug}`} aria-labelledby={`tracker-column-${slug}-title`} onDragOver={(event) => { event.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop} className={`flex flex-col min-w-0 border transition-colors ${over ? "border-[#0F141B] bg-[#F0F2F4]" : "hairline bg-[#F7F8F9]"}`}>
      <header className="px-3 py-3 border-b hairline bg-white">
        <div className="flex items-center justify-between gap-2">
          <h2 id={`tracker-column-${slug}-title`} className="inline-flex items-center gap-2 font-mono-label text-[11px] !tracking-normal text-[#0F141B]"><Icon size={13} aria-hidden="true" style={{ color }} />{status}</h2>
          <span data-testid={`tracker-count-${slug}`} className="font-mono-label text-[11px] text-[#5F6168]">{items.length}</span>
        </div>
        <p className="mt-1 text-[10px] text-[#71747B]">{hint}</p>
      </header>
      <div className="flex-1 p-2.5 space-y-2.5 min-h-[120px]">
        {items.length ? items.map((item) => <TrackerCard key={item.id} item={item} />)
          : <p data-testid={`tracker-empty-${slug}`} className="py-8 text-center text-[11px] text-[#8A94A2] inline-flex w-full items-center justify-center gap-1.5"><CheckCircle2 size={12} aria-hidden="true" />Nothing here yet</p>}
      </div>
    </section>
  );
};

export const TrackerBoard = () => {
  const { items } = useSavedJobs();
  return (
    <div data-testid="tracker-board" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-start">
      {TRACKER_STATUSES.map((status) => <Column key={status} status={status} items={items.filter((item) => item.status === status)} />)}
    </div>
  );
};
